import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import Sidebar from "../components/Sidebar";
import {
  registerUser,
  fetchUsers,
  fetchUserProfile,
  updateUserProfileById,
  getAllDepartments,
  deleteUser,
} from "../api/services";

import CreateUserForm from "../components/CreateUserForm";
import UsersList from "../components/UsersList";
import UserDialogs from "../components/UserDialogs";

// ---- Shared tokens used by the main file only ----
const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: ui.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const MenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: "block", md: "none" },
      position: "sticky",
      top: { xs: "64px", sm: "80px" },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: "linear-gradient(135deg, #009db9 0%, #007a8a 100%)",
          color: "#fff",
          borderRadius: "12px",
          p: 1.2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "&:hover": {
            background: "linear-gradient(135deg, #007a8a 0%, #005b66 100%)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          },
          transition: "all 0.3s ease",
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: "1.6rem" }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: ui.primarySoft, color: ui.primary },
  success: { bg: ui.successSoft, color: ui.success },
  warning: { bg: ui.warningSoft, color: ui.warning },
  danger: { bg: ui.dangerSoft, color: ui.danger },
};

const StatCard = ({ label, value, icon: Icon, tone = "primary" }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: "14px",
        border: `1px solid ${ui.border}`,
        backgroundColor: "#fff",
        boxShadow: ui.shadow,
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ui.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: ui.text,
            fontSize: { xs: "1.05rem", md: "1.2rem" },
            lineHeight: 1.3,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const UserManagement = () => {
  // ---- Create form state ----
  const [fullName, setFullName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  // ---- Users list + pagination ----
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ---- Dialog state ----
  const [openModal, setOpenModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [actionType, setActionType] = useState("");

  // ---- Edit form state ----
  const [editFullName, setEditFullName] = useState("");
  const [editEmployeeId, setEditEmployeeId] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editDepartment, setEditDepartment] = useState("");
  const [editJobTitle, setEditJobTitle] = useState("");

  // ---- Feedback + UI ----
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [departments, setDepartments] = useState([]);

  // ---- Loading flags ----
  const [usersLoading, setUsersLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ---- Snackbar utility ----
  const showSnackbar = useCallback((message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // ---- Load users ----
  const loadUsers = useCallback(async () => {
    try {
      const response = await fetchUsers();

      let userData = [];
      if (response?.data?.content && Array.isArray(response.data.content)) {
        userData = response.data.content;
      } else if (Array.isArray(response)) {
        userData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        userData = response.data;
      } else if (response?.content && Array.isArray(response.content)) {
        userData = response.content;
      } else if (Array.isArray(response) && response[0]?.activeUsers) {
        const userDataObj = response[0];
        const activeUsers = userDataObj.activeUsers || [];
        const deletedUsers = userDataObj.deletedUsers || [];
        userData = [...activeUsers, ...deletedUsers];
      } else if (response && typeof response === "object" && response.id) {
        userData = [response];
      }

      if (!Array.isArray(userData) || userData.length === 0) {
        setUsers([]);
        return;
      }

      const mappedUsers = userData.map((user) => ({
        id: user.id || `user-${Date.now()}-${Math.random()}`,
        fullName: user.name || user.fullName || "Unknown",
        employeeId: user.employeeId || "N/A",
        email: user.email || "No email",
        role: user.role || "EMPLOYEE",
        department: user.department || "N/A",
        jobTitle: user.jobTitle || "N/A",
        createdAt: user.createdAt || new Date().toISOString(),
        deleted: user.deleted || false,
        deletedAt: user.deletedAt || null,
      }));

      setUsers(mappedUsers);
    } catch (error) {
      showSnackbar(
        "Error fetching users: " + (error.message || "Unknown error"),
        "error"
      );
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [showSnackbar]);

  // ---- Load departments ----
  const loadDepartments = useCallback(async () => {
    try {
      const response = await getAllDepartments();
      const fetchedDepartments = response?.data?.content || [];
      setDepartments(fetchedDepartments);
    } catch (error) {
      showSnackbar("Error fetching departments", "error");
      setDepartments([]);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadUsers();
    loadDepartments();
  }, [loadUsers, loadDepartments]);

  // ---- Pagination ----
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---- Create user ----
  const handleSubmit = async () => {
    if (!fullName || !employeeId || !email || !role || !jobTitle || !department) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    const userData = {
      name: fullName,
      employeeId,
      email,
      role,
      department,
      jobTitle,
    };
    setSubmitting(true);
    try {
      const response = await registerUser(userData);
      const newUser = {
        id: response.id || Date.now().toString(),
        fullName: response.name,
        employeeId: response.employeeId || "",
        email: response.email,
        role: response.role,
        department,
        jobTitle: response.jobTitle || "N/A",
        createdAt: new Date().toISOString(),
        deleted: false,
        deletedAt: null,
      };
      setUsers((prevUsers) => [...prevUsers, newUser]);
      setPage(0);
      setFullName("");
      setEmployeeId("");
      setEmail("");
      setRole("");
      setDepartment("");
      setJobTitle("");

      setTimeout(async () => {
        await loadUsers();
      }, 3000);
      showSnackbar("User created successfully!", "success");
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Error creating user",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Open modal / details ----
  const fetchUserDetails = async (id) => {
    try {
      const details = await fetchUserProfile(id);
      setUserDetails(details);
      setOpenDetailsModal(true);
    } catch (error) {
      showSnackbar("Error fetching user details", "error");
    }
  };

  const handleOpenModal = (user, type) => {
    setSelectedUser(user);
    setActionType(type);

    if (type === "Edit") {
      setEditFullName(user.fullName);
      setEditEmployeeId(user.employeeId);
      setEditEmail(user.email);
      setEditRole(user.role);
      setEditDepartment(user.department === "N/A" ? "" : user.department);
      setEditJobTitle(user.jobTitle);
    } else if (type === "Details") {
      fetchUserDetails(user.id);
      return;
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
    setActionType("");
    setEditFullName("");
    setEditEmployeeId("");
    setEditEmail("");
    setEditRole("");
    setEditDepartment("");
    setEditJobTitle("");
  };

  const handleCloseDetailsModal = () => {
    setOpenDetailsModal(false);
    setUserDetails(null);
    setSelectedUser(null);
  };

  // ---- Confirm Edit / Delete ----
  const handleConfirmAction = async () => {
    if (actionType === "Delete") {
      const originalUsers = [...users];
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser.id
            ? { ...user, deleted: true, deletedAt: new Date().toISOString() }
            : user
        )
      );
      setActionLoading(true);
      try {
        const response = await deleteUser(selectedUser.id);
        if (response === "User deleted successfully") {
          await loadUsers();
          showSnackbar("User deleted successfully!", "success");
        } else {
          setUsers(originalUsers);
          showSnackbar("Failed to delete user: Unexpected response", "error");
        }
      } catch (error) {
        setUsers(originalUsers);
        showSnackbar(
          error.response?.data?.message || "Error deleting user",
          "error"
        );
      } finally {
        setActionLoading(false);
      }
    } else if (actionType === "Edit") {
      if (
        !editFullName ||
        !editEmployeeId ||
        !editEmail ||
        !editRole ||
        !editJobTitle
      ) {
        showSnackbar("Please fill all required fields", "error");
        return;
      }
      const selectedDept = departments.find(
        (dept) => dept.id === editDepartment
      );
      if (editDepartment && !selectedDept) {
        showSnackbar("Invalid department selected", "error");
        return;
      }
      setActionLoading(true);
      try {
        await updateUserProfileById(selectedUser.id, {
          name: editFullName,
          employeeId: editEmployeeId,
          email: editEmail,
          role: editRole,
          department: editDepartment || null,
          jobTitle: editJobTitle,
        });
        await loadUsers();
        showSnackbar("User updated successfully!", "success");
      } catch (error) {
        showSnackbar(
          error.response?.data?.message || "Error updating user",
          "error"
        );
      } finally {
        setActionLoading(false);
      }
    }
    handleCloseModal();
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // ---- Derived ----
  const activeCount = users.filter((u) => !u.deleted).length;
  const deletedCount = users.filter((u) => u.deleted).length;
  const pagedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const departmentOptions = Array.isArray(departments) ? departments : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          width: "100%",
          flexGrow: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: "50px", sm: "60px", md: "120px" },
            backgroundColor: "#fff",
            minHeight: "100vh",
            ml: { md: "20px" },
            pr: { xs: 1, md: 3 },
            position: "relative",
          }}
        >
          <MenuButton onClick={handleDrawerToggle} />

          <PageHeader title="User Management" />

          <StatGrid>
            <StatCard
              label="Total users"
              value={users.length.toLocaleString()}
              icon={PeopleAltOutlinedIcon}
            />
            <StatCard
              label="Active"
              value={activeCount.toLocaleString()}
              icon={HowToRegOutlinedIcon}
              tone="success"
            />
            <StatCard
              label="Deleted"
              value={deletedCount.toLocaleString()}
              icon={PersonOffOutlinedIcon}
              tone="danger"
            />
          </StatGrid>

          <CreateUserForm
            fullName={fullName}
            setFullName={setFullName}
            employeeId={employeeId}
            setEmployeeId={setEmployeeId}
            email={email}
            setEmail={setEmail}
            role={role}
            setRole={setRole}
            department={department}
            setDepartment={setDepartment}
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            departmentOptions={departmentOptions}
            submitting={submitting}
            handleSubmit={handleSubmit}
          />

          <UsersList
            usersLoading={usersLoading}
            users={users}
            pagedUsers={pagedUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOpenModal={handleOpenModal}
          />
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: 1100,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
            top: { xs: "64px", sm: "80px" },
            height: "calc(100% - 64px)",
            borderRight: "none",
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <UserDialogs
        openModal={openModal}
        actionType={actionType}
        actionLoading={actionLoading}
        selectedUser={selectedUser}
        handleCloseModal={handleCloseModal}
        handleConfirmAction={handleConfirmAction}
        editFullName={editFullName}
        setEditFullName={setEditFullName}
        editEmployeeId={editEmployeeId}
        setEditEmployeeId={setEditEmployeeId}
        editEmail={editEmail}
        setEditEmail={setEditEmail}
        editRole={editRole}
        setEditRole={setEditRole}
        editDepartment={editDepartment}
        setEditDepartment={setEditDepartment}
        editJobTitle={editJobTitle}
        setEditJobTitle={setEditJobTitle}
        departmentOptions={departmentOptions}
        openDetailsModal={openDetailsModal}
        handleCloseDetailsModal={handleCloseDetailsModal}
        userDetails={userDetails}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;